import LoadingButton from "@mui/lab/LoadingButton";
import { Box, TextField, Link, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { get, isSandbox, requestV2, successfulLoginHandler } from "../utils/io";
import AccountReactivationDialog from "./AccountReactivationDialog";

function Login() {
  const location = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [usernameErr, setUsernameErr] = useState(false);
  const [useSso, setUseSso] = useState(true);
  const [showReactivationDialog, setShowReactivationDialog] = useState(false);
  const [deactivatedUsername, setDeactivatedUsername] = useState("");
  const searchParamRedirect = new URLSearchParams(location.search).get(
    "redirect"
  );
  const redirect = searchParamRedirect
    ? decodeURIComponent(searchParamRedirect)
    : null;
  const theme = useTheme();

  const snackbarError = {
    message: "Unable to login, please try again.",
    variant: "error",
  };

  const lockoutAccountError = {
    message:
      "Your account has been temporary locked for 15 minutes due to too many failed login attempts. Please try again later.",
    variant: "error",
  };

  const onLoginClicked = async () => {
    setLoading(true);

    try {
      const res = await requestV2(
        "post",
        "/auth/token",
        {
          noAuth: true,
          data: { password, username },
        },
        false
      );
      await successfulLoginHandler(res.data);
      const path = isSandbox() ? "/get-started" : "/hubs";
      history.push(redirect || path);
    } catch (err) {
      if (
        err.response &&
        err.response.data?.error_code === "account_deactivated"
      ) {
        setDeactivatedUsername(err.response.data.username || username);
        setShowReactivationDialog(true);
        setLoading(false);
        return;
      }

      if (err.response && err.response.status === 403) {
        enqueueSnackbar(lockoutAccountError);
        setLoading(false);
        return;
      }
      enqueueSnackbar(snackbarError);
      setLoading(false);
    }
  };

  const onLoginWithSsoClicked = async () => {
    if (!username) {
      setUsernameErr(true);
      return;
    }

    const atSignIdx = username.indexOf("@");
    if (atSignIdx === -1) {
      setUseSso(false);
      return;
    }

    setLoading(true);
    const redirectPath =
      new URLSearchParams(location.search).get("redirect") || "/";

    try {
      // TODO add a random value into state and store in sessionStorage for CSRF
      // stateVerifier = crypto.getRandomValues
      const res = await get(
        "/auth/sso/idpurl",
        {
          username,
          redirect_uri: `${window.origin}/auth/oauth-token`,
          state: JSON.stringify({ redirect: redirectPath }),
        },
        { noAuth: true }
      );
      if (res.status === 200) {
        window.location = encodeURI(res.data.idpUrl);
      } else {
        setUseSso(false);
      }
    } catch (err) {
      enqueueSnackbar(snackbarError);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    return useSso ? onLoginWithSsoClicked() : onLoginClicked();
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          border: "2px solid #ccc",
          borderRadius: "4px",
          boxShadow: theme.shadows[5],
          padding: theme.spacing(2, 4),
        })}
      >
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: 350,
          }}
          onSubmit={onSubmit}
        >
          <h3>Sign In</h3>
          <TextField
            autoFocus
            sx={{ margin: theme.spacing(1, 0) }}
            id="outlined-required"
            label="Email/Username"
            onChange={(e) => setUsername(e.target.value)}
            required
            value={username}
            error={usernameErr}
            variant="outlined"
          />

          {!useSso && (
            <>
              <TextField
                autoComplete="current-password"
                sx={{ margin: theme.spacing(1, 0) }}
                id="outlined-password-input"
                label="Password"
                onChange={(e) => setPassword(e.target.value)}
                required
                type="password"
                value={password}
                variant="outlined"
                autoFocus
              />
              <Link mb="10px" href="/password-reset">
                Forgot password?
              </Link>
            </>
          )}

          <LoadingButton loading={loading} variant="contained" type="submit">
            {useSso ? "Continue" : "Login"}
          </LoadingButton>
          <Box mt="10px">
            New to Stel? <Link href="new-organization">Sign up.</Link>
          </Box>
        </form>
      </Box>
      <AccountReactivationDialog
        open={showReactivationDialog}
        onClose={() => setShowReactivationDialog(false)}
        username={deactivatedUsername}
      />
    </>
  );
}

export default Login;
